import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const INITIAL = { q: "", people: [], date_from: "", date_to: "", category: "all", action: "", client_id: "", project_id: "",
    page: 1, page_size: 25, sort_key: "when", sort_dir: "desc" };

export function useAuditBrowser(view) {
    const [filters, setFilters] = useState(INITIAL);
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [options, setOptions] = useState({ people: [], actions: [], clients: [], projects: [] });
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [optionsError, setOptionsError] = useState(false);
    const [revision, setRevision] = useState(0);
    const invalidRange = !!(filters.date_from && filters.date_to && filters.date_from > filters.date_to);

    function change(patch) {
        setFilters((current) => ({ ...current, ...patch, page: patch.page ?? 1 }));
    }
    function clear() {
        setFilters((current) => ({ ...INITIAL, page_size: current.page_size,
            sort_key: current.sort_key, sort_dir: current.sort_dir }));
    }
    function toggleSort(key) {
        setFilters((current) => ({ ...current, page: 1, sort_key: key,
            sort_dir: current.sort_key === key && current.sort_dir === "asc" ? "desc" : "asc" }));
    }
    function retry() { setRevision((current) => current + 1); }

    useEffect(() => {
        const controller = new AbortController();
        let active = true;
        setOptionsLoading(true);
        setOptionsError(false);
        api.get("/audit-log/filter-options", { params: { view }, signal: controller.signal })
            .then(({ data: result }) => { if (active) setOptions(result); })
            .catch(() => { if (active) setOptionsError(true); })
            .finally(() => { if (active) setOptionsLoading(false); });
        return () => { active = false; controller.abort(); };
    }, [view, revision]);

    useEffect(() => {
        const controller = new AbortController();
        let active = true;
        setError("");
        if (invalidRange) {
            setLoading(false);
            return () => { active = false; controller.abort(); };
        }
        setLoading(true);
        // Debounce typing, and cancel superseded requests so older results cannot replace newer filters.
        const timer = setTimeout(() => {
            const params = new URLSearchParams();
            for (const key of ["page", "page_size", "q", "date_from", "date_to", "sort_dir"]) {
                if (filters[key] !== "") params.set(key, String(filters[key]));
            }
            filters.people.forEach((person) => params.append("person", person));
            if (view === "activity") {
                if (filters.category !== "all") params.set("category", filters.category);
            } else {
                params.set("sort_key", filters.sort_key);
                if (filters.action) params.set("action", filters.action);
                if (filters.client_id) params.set("client_id", filters.client_id);
                if (filters.project_id) params.set("project_id", filters.project_id);
            }
            api.get(view === "activity" ? "/audit-log/site-activity/page" : "/audit-log/page",
                { params, signal: controller.signal })
                .then(({ data: result }) => {
                    if (!active) return;
                    const lastPage = Math.max(1, Math.ceil(result.total / filters.page_size));
                    if (filters.page > lastPage) {
                        setFilters((current) => ({ ...current, page: lastPage }));
                        return;
                    }
                    setData(result);
                })
                .catch((failure) => {
                    if (!active) return;
                    setData({ items: [], total: 0 });
                    setError(formatApiError(failure.response?.data?.detail || "Could not load the audit log. Please retry."));
                })
                .finally(() => { if (active) setLoading(false); });
        }, 200);
        return () => { active = false; clearTimeout(timer); controller.abort(); };
    }, [view, filters, invalidRange, revision]);

    return { filters, change, clear, toggleSort, retry, events: data.items, total: data.total,
        loading, error: invalidRange ? "From date must be on or before To date." : error,
        options, optionsLoading, optionsError };
}
